import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, ExternalLink, Newspaper, RefreshCw, Shield, Sparkles } from 'lucide-react';
import { useLorcanaNews } from '@/hooks/useLorcanaNews';
import { Skeleton } from '@/components/ui/skeleton';
import { PersonalizedSuggestions } from '@/components/PersonalizedSuggestions';
import { supabase } from '@/integrations/supabase/client';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { toast } from 'sonner';

type Digest = {
  id: string;
  slug: string;
  title: string;
  meta_description: string | null;
  published_at: string;
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function NewsTab() {
  const { data: items, isLoading, isFetching, refetch, error } = useLorcanaNews();
  const { isAdmin } = useIsAdmin();
  const [digests, setDigests] = useState<Digest[]>([]);

  useEffect(() => {
    supabase
      .from('daily_digests')
      .select('id, slug, title, meta_description, published_at')
      .eq('is_published', true)
      .order('published_at', { ascending: false })
      .limit(5)
      .then(({ data }) => setDigests((data as Digest[]) ?? []));
  }, []);

  async function archiveItem(item: { title: string; link: string; source: string; summary?: string; publishedAt: string }) {
    const { error: err } = await supabase.from('news_articles').insert({
      title: item.title,
      url: item.link,
      source: item.source,
      summary: item.summary ?? null,
      published_at: item.publishedAt,
      tags: [],
    });
    if (err) toast.error(err.message);
    else toast.success('Archived for SEO');
  }

  return (
    <div className="px-4 py-4 space-y-3">
      <PersonalizedSuggestions surface="home" />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Newspaper className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-display font-semibold">Lorcana News</h2>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Link
              to="/admin/news"
              className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-body text-muted-foreground hover:text-foreground"
            >
              <Shield className="h-3.5 w-3.5" /> Admin
            </Link>
          )}
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-body text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Refresh news"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground font-body">
        Auto-refreshes hourly · new sets, tournaments, and Lorcana coverage
      </p>

      {digests.length > 0 && (
        <section className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
            <Sparkles className="h-3.5 w-3.5" /> LoreScout Daily Digest
          </div>
          <ul className="space-y-1.5">
            {digests.map((d) => (
              <li key={d.id}>
                <Link
                  to={`/news/${d.slug}`}
                  className="block rounded-lg p-2 hover:bg-background/60"
                >
                  <p className="font-display text-sm font-semibold leading-snug">{d.title}</p>
                  <p className="text-[11px] text-muted-foreground font-body">
                    {new Date(d.published_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                    {d.meta_description ? ` · ${d.meta_description}` : ''}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/news"
            className="block text-center text-xs font-semibold text-primary hover:underline"
          >
            View all digests →
          </Link>
        </section>
      )}

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm font-body text-destructive">
          Couldn't load news right now. Try again in a moment.
        </div>
      )}

      {!isLoading && items && items.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-6 text-center text-sm font-body text-muted-foreground">
          No recent Lorcana news found.
        </div>
      )}

      <ul className="space-y-3">
        {items?.map((item, idx) => (
          <li key={`${item.link}-${idx}`} className="relative">
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-sm font-semibold leading-snug">
                  {item.title}
                </h3>
                <ExternalLink className="h-4 w-4 flex-shrink-0 text-muted-foreground mt-0.5" />
              </div>
              {item.summary && (
                <p className="mt-2 text-xs font-body text-muted-foreground leading-relaxed line-clamp-3">
                  {item.summary}
                </p>
              )}
              <div className="mt-3 flex items-center gap-2 text-[11px] font-body text-muted-foreground">
                <span className="font-medium text-foreground/80">{item.source}</span>
                <span>·</span>
                <span>{timeAgo(item.publishedAt)}</span>
              </div>
            </a>
            {isAdmin && (
              <button
                onClick={() => archiveItem(item)}
                className="absolute right-2 bottom-2 flex items-center gap-1 rounded-full border border-border bg-background/90 px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
                title="Archive this article on LoreScout for SEO"
              >
                <Archive className="h-3 w-3" /> Archive
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
